import React, { useState, useEffect, useCallback, useRef } from "react";
import { useUiContextStore } from "../../stores/uiContextStore";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router";
import { useTheme } from "next-themes";
import { RowsPerCardSelect } from "../components/EntityCardGrid";
import {
  Globe,
  Network,
  ChevronDown,
  ChevronRight,
  Cloud,
  Server,
  X,
  Tag,
  Code,
  MapPin,
  Activity,
  Clock,
  Search,
  Copy,
  Check,
  Building2,
  Layers,
  Calendar,
  RefreshCw,
  ExternalLink,
  Zap,
  Loader2,
} from "lucide-react";
import {
  getCloudProvider,
  listCloudProviderResources,
  searchCloudProviderResources,
  getAwsResource,
  getCloudTopology,
  CloudProviderResponse,
  AwsResourceListResponse,
  AwsResource,
  CloudTopologyResponse,
  TopologyResource,
  ServiceTopologyGroup,
  VpcTopologyGroup,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { useDiscoveryStore } from "../../stores/discoveryStore";
import { DataTable, Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { Breadcrumb } from "../components/Breadcrumb";
import { Tip } from "../components/ui/tooltip";
import {
  ReactFlow,
  Background,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  getBezierPath,
  type ReactFlowInstance,
  type Node,
  type Edge,
} from "@xyflow/react";
import { topologyInitialZoom } from "./cmdb/cmdbUtils";

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  running:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  available:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  active:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  stopped:
    "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400",
  pending:
    "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400",
  terminated: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  failed: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
  deleting: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="d text-sm">—</span>;
  const cls =
    STATUS_COLORS[status.toLowerCase()] ??
    "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400";
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ── Resource Detail Panel ─────────────────────────────────────────────────────

const SERVICE_ACCENT: Record<
  string,
  { bg: string; text: string; border: string; glow: string }
> = {
  vpc: {
    bg: "from-sky-600/20 to-sky-900/10",
    text: "text-sky-400",
    border: "border-sky-500/30",
    glow: "rgba(56,189,248,0.15)",
  },
  ec2: {
    bg: "from-orange-600/20 to-orange-900/10",
    text: "text-orange-400",
    border: "border-orange-500/30",
    glow: "rgba(251,146,60,0.15)",
  },
  s3: {
    bg: "from-green-600/20 to-green-900/10",
    text: "text-green-400",
    border: "border-green-500/30",
    glow: "rgba(74,222,128,0.15)",
  },
  rds: {
    bg: "from-indigo-600/20 to-indigo-900/10",
    text: "text-indigo-400",
    border: "border-indigo-500/30",
    glow: "rgba(129,140,248,0.15)",
  },
  lambda: {
    bg: "from-amber-600/20 to-amber-900/10",
    text: "text-amber-400",
    border: "border-amber-500/30",
    glow: "rgba(251,191,36,0.15)",
  },
  iam: {
    bg: "from-rose-600/20 to-rose-900/10",
    text: "text-rose-400",
    border: "border-rose-500/30",
    glow: "rgba(251,113,133,0.15)",
  },
};

function getServiceAccent(serviceName: string | null) {
  const key = (serviceName || "").toLowerCase().split(" ")[0];
  return (
    SERVICE_ACCENT[key] ?? {
      bg: "from-indigo-600/20 to-indigo-900/10",
      text: "text-indigo-400",
      border: "border-indigo-500/30",
      glow: "rgba(129,140,248,0.15)",
    }
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };
  return (
    <Tip label="Copy" side="top">
      <button
        onClick={copy}
        className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
      >
        {copied ? (
          <Check className="size-3 text-emerald-400" />
        ) : (
          <Copy className="size-3 d hover:text-foreground" />
        )}
      </button>
    </Tip>
  );
}

function DetailRow({
  label,
  value,
  mono,
  copyable,
  icon,
}: {
  label: string;
  value: string;
  mono?: boolean;
  copyable?: boolean;
  icon?: React.ReactNode;
}) {
  const { theme } = useTheme();
  const dk = theme !== "light";
  return (
    <div className="flex items-start gap-3 py-2.5 last:border-0" style={{ borderBottom: `1px solid ${dk ? "rgba(255,255,255,0.05)" : "#f1f5f9"}` }}>
      <div className="flex items-center gap-1.5 w-36 flex-shrink-0 pt-0.5">
        {icon && <span style={{ color: dk ? "#9ca3af" : "#6b7280" }}>{icon}</span>}
        <span className="text-xs leading-relaxed" style={{ color: dk ? "#9ca3af" : "#6b7280" }}>{label}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        <span
          className={`text-sm break-all ${mono ? "font-mono text-xs leading-relaxed" : ""}`}
          style={{ color: dk ? "#e5e7eb" : "#111827", fontWeight: 500 }}
        >
          {value}
        </span>
        {copyable && value !== "—" && <CopyButton value={value} />}
      </div>
    </div>
  );
}

function PanelSection({
  title,
  icon,
  children,
  defaultOpen = true,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { theme } = useTheme();
  const dk = theme !== "light";
  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        background: dk ? "rgba(255,255,255,0.02)" : "#f9fafb",
        border: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
      }}
    >
      <button
        onClick={() => setOpen((p) => !p)}
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-left transition-colors"
        style={{
          borderBottom: open ? `1px solid ${dk ? "rgba(255,255,255,0.06)" : "#f1f5f9"}` : "none",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = dk ? "rgba(255,255,255,0.05)" : "#f3f4f6")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span style={{ color: dk ? "#60a5fa" : "#111827" }}>{icon}</span>
        <span className="text-xs font-bold tracking-widest flex-1" style={{ color: dk ? "#60a5fa" : "#111827" }}>
          {title}
        </span>
        {open
          ? <ChevronDown className="size-3.5" style={{ color: dk ? "#60a5fa" : "#111827" }} />
          : <ChevronRight className="size-3.5" style={{ color: dk ? "#60a5fa" : "#111827" }} />}
      </button>
      {open && <div className="px-4 pb-1">{children}</div>}
    </div>
  );
}

function ResourceDetailPanel({
  resource,
  onClose,
  contained = false,
}: {
  resource: AwsResource;
  onClose: () => void;
  contained?: boolean;
}) {
  const { theme } = useTheme();
  const dk = theme !== "light";
  const accent = getServiceAccent(resource.serviceName);
  const abbr = (resource.serviceName || "RES").slice(0, 3).toUpperCase();
  const statusLower = (resource.status || "").toLowerCase();
  const statusIsGood = ["running", "available", "active", "in-use"].includes(
    statusLower,
  );
  const statusIsBad = ["stopped", "terminated", "failed", "deleting"].includes(
    statusLower,
  );
  const statusStrip = statusIsGood
    ? "#22c55e"
    : statusIsBad
      ? "#ef4444"
      : statusLower === "pending"
        ? "#eab308"
        : "#475569";

  const tagEntries = resource.tags ? Object.entries(resource.tags) : [];
  const hasRawConfig =
    resource.rawConfig && Object.keys(resource.rawConfig).length > 0;

  return (
    <div className={`${contained ? "absolute" : "fixed"} inset-0 z-50 flex`}>
      {/* Dim overlay */}
      <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div
        className="w-[520px] flex flex-col overflow-hidden shadow-2xl"
        style={{
          background: dk
            ? "linear-gradient(180deg, #080f1e 0%, #050c18 100%)"
            : "#ffffff",
          borderLeft: `1px solid ${dk ? "rgba(255,255,255,0.09)" : "#e2e8f0"}`,
        }}
      >
        {/* Status strip */}
        <div style={{ height: 3, background: statusStrip, flexShrink: 0 }} />

        {/* Hero header */}
        <div
          className={`relative px-6 pt-5 pb-4 ${dk ? `bg-gradient-to-br ${accent.bg}` : ""}`}
          style={{
            background: dk ? undefined : "#f8fafc",
            borderBottom: `1px solid ${dk ? "rgba(255,255,255,0.07)" : "#e2e8f0"}`,
            flexShrink: 0,
          }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg transition-colors"
            style={{ color: dk ? undefined : "#111827" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = dk ? "rgba(255,255,255,0.1)" : "#e5e7eb")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <X className="size-4 d" />
          </button>

          {/* Service badge + resource name */}
          <div className="flex items-start gap-3 pr-8">
            <div
              className={`size-10 rounded-lg flex items-center justify-center flex-shrink-0 border ${accent.border}`}
              style={{
                background: `radial-gradient(circle at center, ${accent.glow}, transparent 70%)`,
                backdropFilter: "blur(4px)",
              }}
            >
              <span className={`text-xs font-bold ${accent.text}`}>{abbr}</span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] tracking-widest mb-0.5" style={{ color: dk ? "#ffffff" : "#374151" }}>
                Resource Detail
              </p>
              <h2 className="font-bold text-base leading-snug break-all" style={{ color: dk ? "#ffffff" : "#111827" }}>
                {resource.resourceName || resource.resourceId}
              </h2>
              {/* {resource.resourceName && (
                <p className="text-xs font-mono mt-0.5 truncate" style={{ color: dk ? "#ffffff" : "#374151" }}>
                  {resource.resourceId}
                </p>
              )} */}
            </div>
          </div>

          {/* Quick stat chips */}
          <div className="flex flex-wrap gap-2 mt-4">
            {resource.status && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
                style={{ background: `${statusStrip}22`, color: statusStrip, border: `1px solid ${statusStrip}44` }}
              >
                <span className="size-1.5 rounded-full flex-shrink-0" style={{ background: statusStrip }} />
                {resource.status}
              </span>
            )}
            {resource.serviceName && (
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${accent.border} ${accent.text}`}
                style={{ background: dk ? "rgba(255,255,255,0.05)" : "#f3f4f6" }}
              >
                <Cloud className="size-3" />
                {resource.serviceName}
              </span>
            )}
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: dk ? "rgba(255,255,255,0.04)" : "#f3f4f6", border: dk ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e5e7eb", color: dk ? "#ffffff" : "#111827" }}
            >
              <MapPin className="size-3" />
              {resource.region}
            </span>
            {resource.isGlobal && (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", color: "#818cf8" }}
              >
                <Globe className="size-3" />
                Global
              </span>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: dk ? undefined : "#ffffff" }}>
          {/* Identity */}
          <PanelSection title="Identity" icon={<Tag className="size-3.5" />}>
            <DetailRow
              label="Resource ID"
              value={resource.resourceId}
              mono
              copyable
              icon={<ExternalLink className="size-3" />}
            />
            <DetailRow label="Name" value={resource.resourceName || "—"} />
            <DetailRow
              label="Service"
              value={resource.serviceName || "—"}
              icon={<Cloud className="size-3" />}
            />
            <DetailRow
              label="Category"
              value={resource.category || "—"}
              icon={<Layers className="size-3" />}
            />
            {resource.provider && (
              <DetailRow
                label="Provider"
                value={resource.provider}
                icon={<Building2 className="size-3" />}
              />
            )}
          </PanelSection>

          {/* Location */}
          <PanelSection title="Location" icon={<MapPin className="size-3.5" />}>
            <DetailRow
              label="Account"
              value={
                resource.accountName
                  ? `${resource.accountName} (${resource.accountId})`
                  : resource.accountId || "—"
              }
              icon={<Building2 className="size-3" />}
            />
            <DetailRow
              label="Region"
              value={resource.region}
              icon={<MapPin className="size-3" />}
            />
            <DetailRow
              label="Availability Zone"
              value={resource.availabilityZone || "—"}
            />
            <DetailRow
              label="VPC"
              value={resource.vpcId || (resource.isGlobal ? "Global" : "—")}
              mono={!!resource.vpcId}
              copyable={!!resource.vpcId}
              icon={<Network className="size-3" />}
            />
          </PanelSection>

          {/* Timeline */}
          <PanelSection title="Timeline" icon={<Clock className="size-3.5" />}>
            <div className="grid grid-cols-2 gap-3 py-3">
              {[
                { icon: <RefreshCw className="size-3.5 mx-auto mb-1.5" />, label: "Last Synced", value: resource.lastSyncedAt ? formatDate(resource.lastSyncedAt) : "—" },
                { icon: <Calendar className="size-3.5 mx-auto mb-1.5" />, label: "Created", value: formatDate(resource.createdAt) },
              ].map((card) => (
                <div
                  key={card.label}
                  className="rounded-lg p-3 text-center"
                  style={{
                    background: dk ? "rgba(255,255,255,0.04)" : "#f9fafb",
                    border: `1px solid ${dk ? "rgba(255,255,255,0.07)" : "#e5e7eb"}`,
                  }}
                >
                  <span style={{ color: dk ? "#ffffff" : "#374151" }}>{card.icon}</span>
                  <p className="text-[10px] tracking-wider mb-0.5" style={{ color: dk ? "#ffffff" : "#374151" }}>{card.label}</p>
                  <p className="text-sm font-semibold" style={{ color: dk ? "#ffffff" : "#111827" }}>{card.value}</p>
                </div>
              ))}
            </div>
          </PanelSection>

          {/* Tags */}
          {tagEntries.length > 0 && (
            <PanelSection
              title={`Tags (${tagEntries.length})`}
              icon={<Tag className="size-3.5" />}
            >
              <div className="flex flex-wrap gap-2 py-3">
                {tagEntries.map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center gap-0 rounded-md overflow-hidden text-xs"
                    style={{ border: `1px solid ${dk ? "rgba(255,255,255,0.1)" : "#e5e7eb"}` }}
                  >
                    <span
                      className="px-2 py-1 font-mono font-semibold"
                      style={{
                        background: dk ? "rgba(255,255,255,0.08)" : "#f3f4f6",
                        color: dk ? "#ffffff" : "#111827",
                        borderRight: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "#e5e7eb"}`,
                      }}
                    >
                      {k}
                    </span>
                    <span
                      className="px-2 py-1"
                      style={{
                        background: dk ? "rgba(255,255,255,0.03)" : "#ffffff",
                        color: dk ? "#ffffff" : "#111827",
                      }}
                    >
                      {v}
                    </span>
                  </div>
                ))}
              </div>
            </PanelSection>
          )}

          {/* Raw Config */}
          {hasRawConfig && (
            <PanelSection
              title="Raw Config"
              icon={<Code className="size-3.5" />}
              defaultOpen={false}
            >
              <div className="relative py-3">
                <div className="absolute top-3 right-0">
                  <CopyButton
                    value={JSON.stringify(resource.rawConfig, null, 2)}
                  />
                </div>
                <pre
                  className="text-xs font-mono rounded-lg p-3 overflow-auto max-h-72 leading-relaxed"
                  style={{
                    background: dk ? "rgba(0,0,0,0.4)" : "#f8fafc",
                    color: dk ? "#7dd3fc" : "#0369a1",
                    border: `1px solid ${dk ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                  }}
                >
                  {JSON.stringify(resource.rawConfig, null, 2)}
                </pre>
              </div>
            </PanelSection>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Topology Canvas ────────────────────────────────────────────────────────────

const TN_W = {
  account: 212,
  region: 178,
  vpc: 178,
  service: 182,
  resource: 196,
};
const TN_H = {
  account: 86,
  region: 70,
  vpc: 70,
  service: 58,
  resource: 72,
};
const H_GAP = 40;
const V_GAP = 72;

// ── Animated Edge ──────────────────────────────────────────────────────────────

function AnimatedFlowEdge(props: any) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
  } = props;
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const healthy = data?.healthy !== false;
  const color = healthy ? "#22c55e" : "#ef4444";
  const dur = (data?.dur as string) ?? "60s";
  const pathId = `epath-${id}`;
  return (
    <g>
      <defs>
        <path id={pathId} d={edgePath} />
      </defs>
      <path d={edgePath} stroke={color} strokeWidth={1.5} fill="none" opacity={0.6} />
      <circle r={3} fill={color} opacity={0.95}>
        <animateMotion
          dur={dur}
          repeatCount="indefinite"
          calcMode="spline"
          keyTimes="0;0.5;1"
          keySplines="0.4 0 0.6 1; 0.4 0 0.6 1"
        >
          <mpath href={`#${pathId}`} />
        </animateMotion>
      </circle>
    </g>
  );
}

// ── Truncated text with custom portal tooltip ──────────────────────────────────

function TruncatedWithTooltip({
  label,
  value,
  style,
}: {
  label?: string;
  value: string;
  style?: React.CSSProperties;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  const [tip, setTip] = React.useState<{ x: number; y: number } | null>(null);

  const onEnter = () => {
    const el = ref.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const r = el.getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.bottom });
  };

  return (
    <>
      <div
        ref={ref}
        style={{ ...style, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
        onMouseEnter={onEnter}
        onMouseLeave={() => setTip(null)}
      >
        {value}
      </div>
      {tip && createPortal(
        <div
          style={{
            position: "fixed",
            left: tip.x,
            top: tip.y + 8,
            transform: "translate(-50%, 0)",
            zIndex: 99999,
            background: "linear-gradient(135deg, #1a2744, #0f1e38)",
            border: "1px solid #2d4a6e",
            borderRadius: 8,
            padding: "7px 12px",
            maxWidth: 280,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)",
            pointerEvents: "none",
          }}
        >
          {label && (
            <div style={{ fontSize: 9, fontWeight: 700, color: "#38bdf8", letterSpacing: "0.1em", marginBottom: 3 }}>
              {label}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#f1f5f9", wordBreak: "break-all", lineHeight: 1.4 }}>
            {value}
          </div>
          <div style={{ position: "absolute", top: -5, left: "50%", width: 8, height: 8, background: "#1a2744", borderLeft: "1px solid #2d4a6e", borderTop: "1px solid #2d4a6e", transform: "translateX(-50%) rotate(45deg)" }} />
        </div>,
        document.body
      )}
    </>
  );
}

// ── Node Components ────────────────────────────────────────────────────────────

function AccountNode({ data }: { data: any }) {
  const dk = data.__isDark !== false;
  return (
    <div
      style={{
        width: TN_W.account,
        height: TN_H.account,
        background: dk
          ? "linear-gradient(135deg,#1a0f00,#261a00)"
          : "linear-gradient(135deg,#fffbeb,#fef9c3)",
        border: "2px solid #f59e0b",
        borderRadius: 16,
        padding: "10px 14px",
        boxShadow: dk
          ? "0 0 32px rgba(245,158,11,0.22), 0 8px 32px rgba(0,0,0,0.7)"
          : "0 0 24px rgba(245,158,11,0.15), 0 4px 16px rgba(0,0,0,0.1)",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        overflow: "hidden", cursor: "grab",
      }}
    >
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={false}
        style={{ background: "#f59e0b", border: `2px solid ${dk ? "#1a0f00" : "#fef3c7"}`, width: 8, height: 8 }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 34, height: 34, borderRadius: 9,
            background: "rgba(245,158,11,0.15)",
            border: "1px solid rgba(245,158,11,0.3)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <Cloud className="size-4" style={{ color: "#f59e0b" }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, color: "#a16207", letterSpacing: "0.08em", fontWeight: 600 }}>
            Cloud Account
          </div>
          <TruncatedWithTooltip
              label="Cloud Account"
              value={data.name}
              style={{ fontSize: 13, fontWeight: 700, color: dk ? "#fde68a" : "#92400e" }}
            />
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ marginLeft: "auto", fontSize: 9, background: "rgba(245,158,11,0.15)", color: dk ? "#f59e0b" : "#b45309", padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>
          {data.total} resources
        </span>
      </div>
    </div>
  );
}

function RegionNode({ data }: { data: any }) {
  const dim = !!data.__dim;
  const dk = data.__isDark !== false;
  const dimBorder = dk ? "#1e2840" : "#e5e7eb";
  return (
    <div
      style={{
        width: TN_W.region,
        height: TN_H.region,
        background: dk ? "linear-gradient(135deg,#1c0d00,#2c1500)" : "linear-gradient(135deg,#fff7ed,#ffedd5)",
        border: `1.5px solid ${dim ? dimBorder : "#f97316"}`,
        borderRadius: 10,
        padding: "7px 12px",
        boxShadow: dim ? "none" : dk
          ? "0 0 20px rgba(249,115,22,0.18), 0 4px 20px rgba(0,0,0,0.6)"
          : "0 2px 12px rgba(249,115,22,0.12)",
        opacity: dim ? 0.3 : 1,
        transition: "opacity 0.3s, border-color 0.3s",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        overflow: "hidden", cursor: "grab",
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} style={{ background: "#f97316", border: `2px solid ${dk ? "#1c0d00" : "#fff"}`, width: 7, height: 7 }} />
      <Handle type="source" position={Position.Right} isConnectable={false} style={{ background: "#f97316", border: `2px solid ${dk ? "#1c0d00" : "#fff"}`, width: 7, height: 7 }} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <MapPin className="size-3" style={{ color: "#f97316" }} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: "#7c2d12", letterSpacing: "0.08em", fontWeight: 600, lineHeight: 1 }}>Region</div>
            <TruncatedWithTooltip
                label="Region"
                value={data.region}
                style={{ fontSize: 12, fontWeight: 700, color: dk ? "#fdba74" : "#7c2d12", lineHeight: 1.3 }}
              />
          </div>
        </div>
        <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
          <span style={{ fontSize: 9, background: "rgba(249,115,22,0.12)", color: dk ? "#fb923c" : "#c2410c", padding: "1px 7px", borderRadius: 999, fontWeight: 700 }}>
            {data.vpcCount} VPC{data.vpcCount !== 1 ? "s" : ""}
          </span>
          <span style={{ fontSize: 9, background: dk ? "rgba(255,255,255,0.05)" : "#f3f4f6", color: dk ? "#cbd5e1" : "#111827", padding: "1px 7px", borderRadius: 999 }}>
            {data.totalResources} res
          </span>
        </div>
      </div>
    </div>
  );
}

function VpcNode({ data }: { data: any }) {
  const dim = !!data.__dim;
  const dk = data.__isDark !== false;
  const accent = data.isGlobal ? "#818cf8" : "#38bdf8";
  const bg = dk
    ? data.isGlobal
      ? "linear-gradient(135deg,#0e0c22,#18163a)"
      : "linear-gradient(135deg,#071628,#0d2247)"
    : data.isGlobal
      ? "linear-gradient(135deg,#eef2ff,#e0e7ff)"
      : "linear-gradient(135deg,#f0f9ff,#e0f2fe)";
  const dimBorder = dk ? "#1e2840" : "#e5e7eb";
  const labelColor = dk
    ? data.isGlobal ? "#4338ca" : "#0369a1"
    : data.isGlobal ? "#4338ca" : "#0284c7";
  const nameColor = dk ? "#e2e8f0" : data.isGlobal ? "#312e81" : "#0c4a6e";
  const idColor = dk ? "#e2e8f0" : "#111827";
  return (
    <div
      style={{
        width: TN_W.vpc,
        height: TN_H.vpc,
        background: bg,
        border: `1.5px solid ${dim ? dimBorder : accent}`,
        borderRadius: 10,
        padding: "7px 12px",
        boxShadow: dim ? "none" : dk
          ? `0 0 20px ${accent}1a, 0 4px 20px rgba(0,0,0,0.6)`
          : `0 2px 12px rgba(0,0,0,0.08)`,
        opacity: dim ? 0.3 : 1,
        transition: "opacity 0.3s",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        overflow: "hidden", cursor: "grab",
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} style={{ background: accent, border: `2px solid ${dk ? "#0d1f3c" : "#fff"}`, width: 7, height: 7 }} />
      <Handle type="source" position={Position.Right} isConnectable={false} style={{ background: accent, border: `2px solid ${dk ? "#0d1f3c" : "#fff"}`, width: 7, height: 7 }} />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 22, height: 22, borderRadius: 6, background: `${accent}18`, border: `1px solid ${accent}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {data.isGlobal ? <Globe className="size-3" style={{ color: accent }} /> : <Network className="size-3" style={{ color: accent }} />}
          </div>
          <div>
            <div style={{ fontSize: 8, color: labelColor, letterSpacing: "0.08em", fontWeight: 600, lineHeight: 1 }}>
              {data.isGlobal ? "Global Services" : "VPC"}
            </div>
            <TruncatedWithTooltip
                label={data.isGlobal ? "Global Services" : "VPC"}
                value={data.vpcName || data.vpcId || "-"}
                style={{ fontSize: 12, fontWeight: 700, color: nameColor, lineHeight: 1.3 }}
              />
          </div>
        </div>
        <div style={{ display: "flex", gap: 5, justifyContent: "center" }}>
          <span style={{ fontSize: 9, background: `${accent}20`, color: accent, padding: "1px 7px", borderRadius: 999, fontWeight: 700 }}>
            {data.svcCount} svc
          </span>
          <span style={{ fontSize: 9, background: dk ? "rgba(255,255,255,0.05)" : "#f3f4f6", color: dk ? "#cbd5e1" : "#111827", padding: "1px 7px", borderRadius: 999 }}>
            {data.total} res
          </span>
          {data.region && (
            <span style={{ fontSize: 9, background: dk ? "rgba(255,255,255,0.04)" : "#f3f4f6", color: dk ? "#e2e8f0" : "#111827", padding: "1px 7px", borderRadius: 999 }}>
              {data.region}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const SVC_ACCENT_COLORS: Record<string, string> = {
  EC2: "#f97316",
  VPC: "#38bdf8",
  S3: "#22c55e",
  RDS: "#818cf8",
  Lambda: "#f59e0b",
  IAM: "#f43f5e",
  ELB: "#06b6d4",
  EKS: "#6366f1",
  "Route 53": "#a855f7",
  CloudFront: "#e879f9",
  SNS: "#fbbf24",
  SQS: "#34d399",
};

function ServiceNode({ data }: { data: any }) {
  const dim = !!data.__dim;
  const sel = !!data.__selected;
  const dk = data.__isDark !== false;
  const abbr = (data.iconKey || data.serviceName || "SVC").slice(0, 3).toUpperCase();
  const accent = "#818cf8";
  const selColor = dk ? "#ffffff" : "#000000";
  const bg = dim
    ? dk ? "linear-gradient(135deg,#0f172a,#172035)" : `${accent}05`
    : sel
      ? dk ? "linear-gradient(135deg,#1c2133,#232d45)" : "#f8fafc"
      : dk ? `linear-gradient(135deg,${accent}12,${accent}1e)` : `${accent}18`;
  const border = dim
    ? `1px solid ${dk ? "#1a2035" : "#e5e7eb"}`
    : sel
      ? `2px solid ${selColor}`
      : `1.5px solid ${accent}`;
  const shadow = dim ? "none"
    : sel
      ? dk
        ? `0 0 20px rgba(255,255,255,0.2), 0 4px 16px rgba(0,0,0,0.6)`
        : `0 0 0 3px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.15)`
      : dk
        ? `0 0 12px ${accent}35, 0 2px 12px rgba(0,0,0,0.5)`
        : `0 0 10px ${accent}30, 0 2px 8px rgba(0,0,0,0.07)`;
  return (
    <div
      style={{
        width: TN_W.service,
        height: TN_H.service,
        background: bg,
        border,
        borderRadius: 9,
        padding: "7px 11px",
        cursor: "grab",
        boxShadow: shadow,
        opacity: dim ? 0.25 : 1,
        transition: "opacity 0.3s, box-shadow 0.2s",
        display: "flex", flexDirection: "column", justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} style={{ background: accent, border: `2px solid ${dk ? "#0f172a" : "#fff"}`, width: 6, height: 6 }} />
        <Handle type="source" position={Position.Right} isConnectable={false} style={{ background: accent, border: `2px solid ${dk ? "#0f172a" : "#fff"}`, width: 6, height: 6 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: `${accent}25`, border: `1px solid ${accent}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 8, fontWeight: 800, color: accent }}>{abbr}</span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <TruncatedWithTooltip
              label="Service"
              value={data.serviceName}
              style={{ fontSize: 10, fontWeight: 600, color: accent }}
            />
          <TruncatedWithTooltip
              label="Category"
              value={data.category}
              style={{ fontSize: 8, color: dk ? "#cbd5e1" : "#6b7280" }}
            />
        </div>
        <span style={{ fontSize: 9, background: `${accent}18`, color: accent, padding: "1px 5px", borderRadius: 999, flexShrink: 0 }}>
          {data.resourceCount}
        </span>
      </div>
    </div>
  );
}

function ResourceNode({ data }: { data: any }) {
  const dim = !!data.__dim;
  const dk = data.__isDark !== false;
  const v = (data.status || "").toLowerCase();
  const isRunning = ["running", "active", "available", "in-use"].includes(v);
  const isPending = ["pending", "starting", "creating"].includes(v);
  const isStopped = !isRunning && !isPending;
  const statusColor = isRunning ? "#22c55e" : isPending ? "#eab308" : "#ef4444";
  const isColored = true;
  const cardBg = dim
    ? dk ? "linear-gradient(135deg,#060e1c,#0c1a2e)" : "#ffffff"
    : isColored
      ? dk ? `linear-gradient(135deg,${statusColor}12,${statusColor}1e)` : `${statusColor}18`
      : dk ? "linear-gradient(135deg,#060e1c,#0c1a2e)" : "#ffffff";
  const cardBorder = dim
    ? `1px solid ${dk ? "#0d1a2e" : "#d1d5db"}`
    : isColored
      ? `1.5px solid ${statusColor}`
      : `1px solid ${statusColor}55`;
  const cardShadow = dim ? "none"
    : isColored
      ? dk ? `0 0 12px ${statusColor}40, 0 2px 12px rgba(0,0,0,0.5)` : `0 0 10px ${statusColor}30, 0 2px 8px rgba(0,0,0,0.07)`
      : dk ? "0 2px 12px rgba(0,0,0,0.6)" : "0 1px 6px rgba(0,0,0,0.07)";
  return (
    <div
      style={{
        width: TN_W.resource,
        height: TN_H.resource,
        background: cardBg,
        border: cardBorder,
        borderRadius: 8,
        padding: "6px 10px",
        cursor: "grab",
        boxShadow: cardShadow,
        opacity: dim ? 0.25 : 1,
        transition: "opacity 0.3s",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        overflow: "hidden",
      }}
    >
      <Handle type="target" position={Position.Left} isConnectable={false} style={{ background: dk ? "#0f2846" : "#cbd5e1", border: `1px solid ${dk ? "#1e3a5f" : "#e2e8f0"}`, width: 5, height: 5 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, flexShrink: 0, boxShadow: `0 0 5px ${statusColor}` }} />
        <TruncatedWithTooltip
            label="Resource"
            value={data.resourceName}
            style={{ fontSize: 10, fontWeight: 600, color: dk ? "#cbd5e1" : "#111827", flex: 1 }}
          />
      </div>
      <TruncatedWithTooltip
          label="Resource ID"
          value={data.resourceId}
          style={{ fontSize: 8, fontFamily: "monospace", color: dk ? "#cbd5e1" : "#111827", marginBottom: 2 }}
        />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 8, background: `${statusColor}18`, color: statusColor, padding: "1px 5px", borderRadius: 999, fontWeight: 600 }}>
          {data.status || "—"}
        </span>
        {data.availabilityZone && (
          <span style={{ fontSize: 7, color: dk ? "#94a3b8" : "#111827" }}>
            {data.availabilityZone}
          </span>
        )}
      </div>
    </div>
  );
}

const tnNodeTypes = {
  tnAccount: AccountNode,
  tnRegion: RegionNode,
  tnVpc: VpcNode,
  tnService: ServiceNode,
  tnResource: ResourceNode,
};

const tnEdgeTypes = {
  animated: AnimatedFlowEdge,
};

// ── Service Resource Side Panel ────────────────────────────────────────────────

function SvcResourcePanel({
  service,
  onClose,
  onView,
}: {
  service: any;
  onClose: () => void;
  onView: (id: string) => void;
}) {
  const { theme } = useTheme();
  const dk = theme !== "light";
  const dot = (s: string | null) => {
    const v = (s ?? "").toLowerCase();
    return ["running", "active", "available", "in-use"].includes(v)
      ? "#22c55e"
      : ["pending", "starting", "creating"].includes(v)
        ? "#eab308"
        : ["stopped", "failed", "terminated"].includes(v)
          ? "#ef4444"
          : "#6b7280";
  };
  return (
    <div
      className="absolute top-0 right-0 h-full w-80 flex flex-col z-20"
      style={{
        background: dk ? "rgba(6,10,22,0.97)" : "#ffffff",
        backdropFilter: "blur(8px)",
        borderLeft: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
        boxShadow: dk ? "none" : "-4px 0 20px rgba(0,0,0,0.08)",
      }}
    >
      <div
        className="flex items-start justify-between px-4 py-3"
        style={{ borderBottom: `1px solid ${dk ? "rgba(255,255,255,0.08)" : "#f1f5f9"}` }}
      >
        <div className="min-w-0">
          <p style={{ fontSize: 10, color: dk ? "#ffffff" : "#374151", letterSpacing: "0.05em" }}>
            {service.category}
          </p>
          <p className="text-sm font-bold truncate" style={{ color: dk ? "#ffffff" : "#111827" }}>
            {service.serviceName}
          </p>
          <p style={{ fontSize: 11, color: dk ? "#ffffff" : "#374151", marginTop: 2 }}>
            {service.resourceCount} Resources
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded ml-2 flex-shrink-0 transition-colors"
          style={{ color: dk ? "#ffffff" : "#111827" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = dk ? "rgba(255,255,255,0.1)" : "#f3f4f6")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <X className="size-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {service.resources.length === 0 ? (
          <div className="py-10 text-center text-sm" style={{ color: dk ? "#ffffff" : "#374151" }}>
            No resources
          </div>
        ) : (
          service.resources.map((res: TopologyResource) => (
            <div
              key={res.id}
              className="flex items-start gap-3 px-4 py-2.5 group transition-colors"
              style={{ borderBottom: `1px solid ${dk ? "rgba(255,255,255,0.04)" : "#f1f5f9"}` }}
              onMouseEnter={(e) => (e.currentTarget.style.background = dk ? "rgba(255,255,255,0.04)" : "#f9fafb")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              <span
                className="mt-1.5 size-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: dot(res.status) }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: dk ? "#ffffff" : "#111827" }}>
                  {res.resourceName || res.resourceId}
                </p>
                <p className="text-[10px] font-mono truncate" style={{ color: dk ? "#ffffff" : "#374151" }}>
                  {res.resourceId}
                </p>
              </div>
              <button
                onClick={() => onView(res.id)}
                className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5"
                style={{ color: "#6366f1" }}
              >
                View →
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── Floating Controls ──────────────────────────────────────────────────────────

function TopologyControls({
  onZoomIn,
  onZoomOut,
  onFitView,
  onFullscreen,
  locked,
  onToggleLock,
  isFullscreen,
  isDark,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onFullscreen: () => void;
  locked: boolean;
  onToggleLock: () => void;
  isFullscreen: boolean;
  isDark: boolean;
}) {
  const btns = [
    { icon: "+", title: "Zoom In", action: onZoomIn },
    { icon: "−", title: "Zoom Out", action: onZoomOut },
    { icon: "⊡", title: "Fit to Screen", action: onFitView },
    {
      icon: isFullscreen ? "⊠" : "⊞",
      title: isFullscreen ? "Exit Fullscreen" : "Fullscreen",
      action: onFullscreen,
    },
    {
      icon: locked ? "🔒︎" : "🔓︎",
      title: locked ? "Unlock Layout" : "Lock Layout",
      action: onToggleLock,
    },
  ];
  const panelBg = isDark ? "rgba(7,14,30,0.92)" : "rgba(255,255,255,0.95)";
  const panelBorder = isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0";
  const btnBg = isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6";
  const btnBorder = isDark ? "rgba(255,255,255,0.1)" : "#d1d5db";
  const btnColor = isDark ? "#e2e8f0" : "#111827";
  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: panelBg,
        border: `1px solid ${panelBorder}`,
        borderRadius: 12,
        padding: "8px 6px",
        backdropFilter: "blur(8px)",
        boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.5)" : "0 4px 16px rgba(0,0,0,0.1)",
      }}
    >
      {btns.map((b) => (
        <Tip key={b.title} label={b.title} side="top">
        <button
          onClick={b.action}
          style={{
            width: 34,
            height: 34,
            borderRadius: 8,
            border: `1px solid ${btnBorder}`,
            background: btnBg,
            color: btnColor,
            cursor: "pointer",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              "rgba(99,102,241,0.2)";
            (e.currentTarget as HTMLButtonElement).style.color = "#6366f1";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = btnBg;
            (e.currentTarget as HTMLButtonElement).style.color = btnColor;
          }}
        >
          {b.icon}
        </button>
        </Tip>
      ))}
    </div>
  );
}

// ── Graph Layout Builder ───────────────────────────────────────────────────────

// Zoom bounds for the topology canvas. Shared with topologyInitialZoom() so the
// data-driven starting zoom can never land outside what the canvas allows.
const TOPO_MIN_ZOOM = 0.05;
const TOPO_MAX_ZOOM = 2.5;

function buildTopologyGraph(
  topo: CloudTopologyResponse,
  expandedRegions: Set<string>,
  expandedVpcs: Set<string>,
  regionFilter: string,
  vpcFilter: string,
  serviceFilter: string,
  isDark = true,
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  type GrpType = VpcTopologyGroup & { isGlobal: boolean };
  const allGroups: GrpType[] = [
    ...topo.vpcs.map((v) => ({ ...v, isGlobal: false })),
    ...(topo.globalServices.totalResources > 0
      ? [{ ...topo.globalServices, isGlobal: true }]
      : []),
  ];

  const regionMap = new Map<string, GrpType[]>();
  allGroups.forEach((g) => {
    const r = g.region || "global";
    if (!regionMap.has(r)) regionMap.set(r, []);
    regionMap.get(r)!.push(g);
  });

  const allRegionEntries = [...regionMap.entries()];
  const visRegionEntries =
    regionFilter === "all"
      ? allRegionEntries
      : allRegionEntries.filter(([r]) => r === regionFilter);

  const vpcKey = (g: GrpType) => g.vpcName || g.vpcId || "Global Services";

  // Left-to-right layout: X axis = hierarchy level, Y axis = sibling spread

  const svcBandH = (sg: ServiceTopologyGroup, resExpanded: boolean): number => {
    if (!resExpanded || sg.resources.length === 0) return TN_H.service;
    return Math.max(
      TN_H.service,
      sg.resources.length * TN_H.resource +
        Math.max(0, sg.resources.length - 1) * H_GAP,
    );
  };

  const vpcBandH = (grp: GrpType, vpcId: string): number => {
    const isExpanded = expandedVpcs.has(vpcId) || vpcFilter === vpcKey(grp);
    if (!isExpanded) return TN_H.vpc;
    const svcs =
      serviceFilter === "all"
        ? grp.serviceGroups
        : grp.serviceGroups.filter((sg) => sg.serviceName === serviceFilter);
    if (svcs.length === 0) return TN_H.vpc;
    const resExp = serviceFilter !== "all";
    return Math.max(
      TN_H.vpc,
      svcs.reduce(
        (s, sg, i) => s + svcBandH(sg, resExp) + (i > 0 ? H_GAP : 0),
        0,
      ),
    );
  };

  const regBandH = (vpcs: GrpType[], regIdx: number): number => {
    const regId = `reg-${regIdx}`;
    const isExpanded = expandedRegions.has(regId) || regionFilter !== "all";
    if (!isExpanded) return TN_H.region;
    const visVpcs =
      vpcFilter === "all" ? vpcs : vpcs.filter((g) => vpcKey(g) === vpcFilter);
    if (visVpcs.length === 0) return TN_H.region;
    return Math.max(
      TN_H.region,
      visVpcs.reduce((s, g, i) => {
        const origIdx = vpcs.indexOf(g);
        return s + vpcBandH(g, `vpc-${regIdx}-${origIdx}`) + (i > 0 ? H_GAP : 0);
      }, 0),
    );
  };

  const regHeights = visRegionEntries.map(([regionName]) => {
    const origIdx = allRegionEntries.findIndex(([r]) => r === regionName);
    return regBandH(allRegionEntries[origIdx][1], origIdx);
  });
  const totalH = regHeights.reduce(
    (s, h, i) => s + h + (i > 0 ? H_GAP : 0),
    0,
  );

  // Fixed X positions for each hierarchy level
  const X0 = 0;
  const X1 = X0 + TN_W.account + V_GAP;
  const X2 = X1 + TN_W.region + V_GAP;
  const X3 = X2 + TN_W.vpc + V_GAP;
  const X4 = X3 + TN_W.service + V_GAP;

  nodes.push({
    id: "__account",
    type: "tnAccount",
    position: { x: X0, y: Math.max(0, (totalH - TN_H.account) / 2) },
    data: {
      name: topo.accountName,
      accountId: topo.accountId,
      region: topo.region,
      total: topo.totalResources,
      __isDark: isDark,
    },
  });

  let curY = 0;
  visRegionEntries.forEach(([regionName], visIdx) => {
    const origIdx = allRegionEntries.findIndex(([r]) => r === regionName);
    const vpcs = allRegionEntries[origIdx][1];
    const regId = `reg-${origIdx}`;
    const regH = regHeights[visIdx];
    const isRegExp = expandedRegions.has(regId) || regionFilter !== "all";

    nodes.push({
      id: regId,
      type: "tnRegion",
      position: { x: X1, y: curY + (regH - TN_H.region) / 2 },
      data: {
        region: regionName,
        vpcCount: vpcs.length,
        expanded: isRegExp,
        totalResources: vpcs.reduce((s, g) => s + g.totalResources, 0),
        __isDark: isDark,
      },
    });
    edges.push({
      id: `e-acc-${regId}`,
      source: "__account",
      target: regId,
      type: "animated",
      data: { healthy: true, dur: "8s" },
    });

    if (isRegExp) {
      const visVpcs =
        vpcFilter === "all"
          ? vpcs
          : vpcs.filter((g) => vpcKey(g) === vpcFilter);
      let vpcCurY = curY;

      visVpcs.forEach((grp) => {
        const origVpcIdx = vpcs.indexOf(grp);
        const vpcId = `vpc-${origIdx}-${origVpcIdx}`;
        const isVpcExp = expandedVpcs.has(vpcId) || vpcFilter === vpcKey(grp);
        const bandH = vpcBandH(grp, vpcId);
        const svcs =
          serviceFilter === "all"
            ? grp.serviceGroups
            : grp.serviceGroups.filter(
                (sg) => sg.serviceName === serviceFilter,
              );

        nodes.push({
          id: vpcId,
          type: "tnVpc",
          position: { x: X2, y: vpcCurY + (bandH - TN_H.vpc) / 2 },
          data: {
            vpcName: grp.vpcName,
            vpcId: grp.vpcId,
            region: grp.region,
            total: grp.totalResources,
            isGlobal: grp.isGlobal,
            svcCount: svcs.length,
            expanded: isVpcExp,
            __isDark: isDark,
          },
        });
        edges.push({
          id: `e-${regId}-${vpcId}`,
          source: regId,
          target: vpcId,
          type: "animated",
          data: { healthy: true, dur: "8s" },
        });

        if (isVpcExp && svcs.length > 0) {
          const svcsTotalH = svcs.reduce(
            (s, sg, i) =>
              s + svcBandH(sg, serviceFilter !== "all") + (i > 0 ? H_GAP : 0),
            0,
          );
          const svcStartY = vpcCurY + bandH / 2 - svcsTotalH / 2;
          let svcCurY = svcStartY;

          svcs.forEach((sg, si) => {
            const svcId = `svc-${origIdx}-${origVpcIdx}-${si}`;
            const sh = svcBandH(sg, serviceFilter !== "all");

            nodes.push({
              id: svcId,
              type: "tnService",
              position: { x: X3, y: svcCurY + (sh - TN_H.service) / 2 },
              data: {
                serviceName: sg.serviceName,
                category: sg.category,
                iconKey: sg.iconKey,
                resourceCount: sg.resourceCount,
                resources: sg.resources,
                __selected: false,
                __isDark: isDark,
              },
            });
            edges.push({
              id: `e-${vpcId}-${svcId}`,
              source: vpcId,
              target: svcId,
              type: "animated",
              data: { healthy: true, dur: "8s" },
            });

            if (serviceFilter !== "all" && sg.resources.length > 0) {
              const resTotalH =
                sg.resources.length * TN_H.resource +
                Math.max(0, sg.resources.length - 1) * H_GAP;
              const resStartY = svcCurY + sh / 2 - resTotalH / 2;

              sg.resources.forEach((res, ri) => {
                const resId = `res-${origIdx}-${origVpcIdx}-${si}-${ri}`;
                const statusGood = [
                  "running",
                  "active",
                  "available",
                  "in-use",
                ].includes((res.status || "").toLowerCase());

                nodes.push({
                  id: resId,
                  type: "tnResource",
                  position: {
                    x: X4,
                    y: resStartY + ri * (TN_H.resource + H_GAP),
                  },
                  data: {
                    resourceName: res.resourceName || res.resourceId,
                    resourceId: res.resourceId,
                    status: res.status,
                    availabilityZone: res.availabilityZone,
                    id: res.id,
                    __isDark: isDark,
                  },
                });
                edges.push({
                  id: `e-${svcId}-${resId}`,
                  source: svcId,
                  target: resId,
                  type: "animated",
                  data: { healthy: statusGood, dur: "8s" },
                });
              });
            }

            svcCurY += sh + H_GAP;
          });
        }

        vpcCurY += bandH + H_GAP;
      });
    }

    curY += regH + H_GAP;
  });

  return { nodes, edges };
}

// ── Cloud Topology View ────────────────────────────────────────────────────────

function CloudTopologyView({
  providerId,
}: {
  providerId: string;
}) {
  const [topo, setTopo] = useState<CloudTopologyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [topoResource, setTopoResource] = useState<AwsResource | null>(null);
  const [loadingTopoDetail, setLoadingTopoDetail] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [flowRef, setFlowRef] = useState<ReactFlowInstance<Node, Edge> | null>(null);
  const [selectedSvc, setSelectedSvc] = useState<any>(null);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(
    new Set(),
  );
  const [expandedVpcs, setExpandedVpcs] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [vpcFilter, setVpcFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [locked, setLocked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hiddenLegend, setHiddenLegend] = useState<Set<string>>(new Set());
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const nodesRef = React.useRef<Node[]>([]);
  nodesRef.current = nodes;
  const initialZoomRef = React.useRef<number | null>(null);
  const [fitViewMode, setFitViewMode] = useState(false);

  const { theme } = useTheme();
  const isDark = theme !== "light";

  const th = {
    containerBg: isDark ? "#06101e" : "#ffffff",
    toolbarBg: isDark ? "rgba(6,16,30,0.96)" : "#ffffff",
    toolbarBorder: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
    rowBorder: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
    textMuted: isDark ? "#ffffff" : "#111827",
    textStrong: isDark ? "#ffffff" : "#111827",
    btnBg: isDark ? "rgba(255,255,255,0.06)" : "#f3f4f6",
    btnBorder: isDark ? "rgba(255,255,255,0.1)" : "#d1d5db",
    btnColor: isDark ? "#ffffff" : "#111827",
    inputBg: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
    inputBorder: isDark ? "rgba(255,255,255,0.1)" : "#d1d5db",
    inputColor: isDark ? "#ffffff" : "#111827",
    iconColor: isDark ? "#ffffff" : "#111827",
    chevronColor: isDark ? "#1e3a5f" : "#cbd5e1",
    hintColor: isDark ? "#ffffff" : "#111827",
    legendBg: isDark ? "rgba(255,255,255,0.03)" : "#f9fafb",
    legendBorder: isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb",
    legendColor: isDark ? "#ffffff" : "#111827",
    bgPatternColor: isDark ? "#1a2e44" : "#f8fafc",
    minimapBg: isDark ? "rgba(6,16,30,0.92)" : "#ffffff",
    minimapMask: isDark ? "rgba(6,16,30,0.85)" : "rgba(241,245,249,0.85)",
    minimapBorder: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0",
  };

  useEffect(() => {
    let isCurrent = true;
    setLoading(true);
    setTopo(null);
    setNodes([]);
    setEdges([]);
    setExpandedRegions(new Set());
    setExpandedVpcs(new Set());
    getCloudTopology(providerId)
      .then((res) => {
        if (!isCurrent) return;
        const data = res.data;
        setTopo(data);
        const allGrps = [
          ...data.vpcs,
          ...(data.globalServices.totalResources > 0
            ? [data.globalServices]
            : []),
        ];
        const regionOrder: string[] = [];
        allGrps.forEach((g) => {
          const r = g.region || "global";
          if (!regionOrder.includes(r)) regionOrder.push(r);
        });
        setExpandedRegions(new Set(regionOrder.map((_, i) => `reg-${i}`)));
        const rm = new Map<string, typeof allGrps>();
        allGrps.forEach((g) => {
          const r = g.region || "global";
          if (!rm.has(r)) rm.set(r, []);
          rm.get(r)!.push(g);
        });
        const allVpcIds = new Set<string>();
        [...rm.values()].forEach((vpcs, ri) =>
          vpcs.forEach((_, vi) => allVpcIds.add(`vpc-${ri}-${vi}`)),
        );
        setExpandedVpcs(allVpcIds);
      })
      .catch(() => {
        if (isCurrent) {
          useToastStore.getState().showError("Error", "Failed to load topology");
        }
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [providerId]);

  useEffect(() => {
    if (!topo) return;
    const { nodes: n, edges: e } = buildTopologyGraph(
      topo,
      expandedRegions,
      expandedVpcs,
      regionFilter,
      vpcFilter,
      serviceFilter,
      isDark,
    );
    setNodes(n);
    setEdges(e);
  }, [topo, expandedRegions, expandedVpcs, regionFilter, vpcFilter, serviceFilter, isDark]);

  const handleViewTopoResource = async (resourceId: string) => {
    setLoadingTopoDetail(true);
    try {
      const res = await getAwsResource(resourceId);
      setTopoResource(res.data);
    } catch {
      useToastStore.getState().showError("Error", "Failed to load resource detail");
    } finally {
      setLoadingTopoDetail(false);
    }
  };

  const handleNodeClick = useCallback(
    (_: any, node: Node) => {
      if (node.type === "tnRegion") {
        setExpandedRegions((prev) => {
          const next = new Set(prev);
          if (next.has(node.id)) {
            next.delete(node.id);
            const ri = node.id.replace("reg-", "");
            setExpandedVpcs((v) => {
              const nv = new Set(v);
              [...nv].forEach((id) => {
                if (id.startsWith(`vpc-${ri}-`)) nv.delete(id);
              });
              return nv;
            });
          } else {
            next.add(node.id);
          }
          return next;
        });
        setSelectedSvc(null);
        return;
      }
      if (node.type === "tnVpc") {
        setExpandedVpcs((prev) => {
          const next = new Set(prev);
          if (next.has(node.id)) next.delete(node.id);
          else next.add(node.id);
          return next;
        });
        setSelectedSvc(null);
        return;
      }
      if (node.type === "tnResource") {
        handleViewTopoResource(node.data.id as string);
        return;
      }
      if (node.type !== "tnService") return;
      const d = node.data as any;
      const alreadySel = !!d.__selected;
      setNodes((nds) =>
        nds.map((n) =>
          n.type === "tnService"
            ? {
                ...n,
                data: {
                  ...n.data,
                  __selected: !alreadySel && n.id === node.id,
                },
              }
            : n,
        ),
      );
      setSelectedSvc(alreadySel ? null : { ...d, __selected: false });
    },
    [handleViewTopoResource],
  );

  const allTopoGroups = React.useMemo(() => {
    if (!topo) return [];
    return [
      ...topo.vpcs,
      ...(topo.globalServices.totalResources > 0
        ? [topo.globalServices]
        : []),
    ];
  }, [topo]);

  const regionNames = React.useMemo(() => {
    const seen: string[] = [];
    allTopoGroups.forEach((g) => {
      const r = g.region || "global";
      if (!seen.includes(r)) seen.push(r);
    });
    return seen;
  }, [allTopoGroups]);

  const vpcsForFilter = React.useMemo(() => {
    const keys: string[] = [];
    allTopoGroups.forEach((g) => {
      const k = g.vpcName || g.vpcId || "Global Services";
      if (!keys.includes(k)) keys.push(k);
    });
    return keys;
  }, [allTopoGroups]);

  const servicesForFilter = React.useMemo(() => {
    if (vpcFilter === "all") return [];
    const vpc = allTopoGroups.find(
      (g) => (g.vpcName || g.vpcId || "Global Services") === vpcFilter,
    );
    return vpc ? vpc.serviceGroups.map((sg) => sg.serviceName) : [];
  }, [allTopoGroups, vpcFilter]);

  const nodeLabel = (n: Node): string =>
    [
      n.data.vpcName,
      n.data.vpcId,
      n.data.serviceName,
      n.data.category,
      n.data.resourceName,
      n.data.resourceId,
      n.data.region,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

  const handleSearch = useCallback(
    (q: string) => {
      setSearchQuery(q);
      const query = q.trim().toLowerCase();
      setNodes((nds) =>
        nds.map((n) => {
          if (!query || n.id === "__account")
            return { ...n, data: { ...n.data, __dim: false } };
          return {
            ...n,
            data: { ...n.data, __dim: !nodeLabel(n).includes(query) },
          };
        }),
      );
      if (query) {
        const match = nodesRef.current.find(
          (n) => n.id !== "__account" && nodeLabel(n).includes(query),
        );
        if (match && flowRef) {
          const nw =
            match.type === "tnAccount"
              ? TN_W.account
              : match.type === "tnRegion"
                ? TN_W.region
                : match.type === "tnVpc"
                  ? TN_W.vpc
                  : match.type === "tnService"
                    ? TN_W.service
                    : TN_W.resource;
          flowRef.setCenter(match.position.x + nw / 2, match.position.y + 40, {
            duration: 500,
            zoom: 1.4,
          });
        }
      }
    },
    [flowRef],
  );

  const handleRegionFilter = useCallback(
    (v: string) => {
      setRegionFilter(v);
      setVpcFilter("all");
      setServiceFilter("all");
      setSearchQuery("");
      setTimeout(
        () => flowRef?.fitView({ padding: 0.25, duration: 400 }),
        150,
      );
    },
    [flowRef],
  );

  const handleVpcFilter = useCallback(
    (v: string) => {
      setVpcFilter(v);
      setServiceFilter("all");
      setSearchQuery("");
      if (v !== "all") {
        const vpc = allTopoGroups.find(
          (g) => (g.vpcName || g.vpcId || "Global Services") === v,
        );
        if (vpc?.region) setRegionFilter(vpc.region);
      }
      setTimeout(
        () => flowRef?.fitView({ padding: 0.25, duration: 400 }),
        150,
      );
    },
    [flowRef, allTopoGroups],
  );

  const handleServiceFilter = useCallback(
    (v: string) => {
      setServiceFilter(v);
      setSearchQuery("");
      setTimeout(
        () => flowRef?.fitView({ padding: 0.2, duration: 400 }),
        150,
      );
    },
    [flowRef],
  );

  const handleExpandAll = useCallback(
    () => setExpandedRegions(new Set(regionNames.map((_, i) => `reg-${i}`))),
    [regionNames],
  );

  const handleCollapseAll = useCallback(() => {
    setExpandedRegions(new Set());
    setExpandedVpcs(new Set());
    setSelectedSvc(null);
    setTimeout(
      () => flowRef?.fitView({ padding: 0.25, duration: 400 }),
      150,
    );
  }, [flowRef]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const toggleLegend = useCallback((label: string) => {
    setHiddenLegend((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const LEGEND_NODE_TYPE: Record<string, string> = {
    Account: "tnAccount",
    Region: "tnRegion",
    VPC: "tnVpc",
    Service: "tnService",
    Resource: "tnResource",
  };

  // Parent → children map built from edges, used for cascade hiding
  const childrenMap = React.useMemo(() => {
    const map = new Map<string, Set<string>>();
    edges.forEach((e) => {
      if (!map.has(e.source)) map.set(e.source, new Set());
      map.get(e.source)!.add(e.target);
    });
    return map;
  }, [edges]);

  // Child → parent map built from edges, used for hover ancestor traversal
  const parentMap = React.useMemo(() => {
    const map = new Map<string, string>();
    edges.forEach((e) => { map.set(e.target, e.source); });
    return map;
  }, [edges]);

  // IDs of the hovered node + all its ancestors up to Account root
  const ancestorIds = React.useMemo(() => {
    if (!hoveredNodeId) return new Set<string>();
    const ids = new Set<string>();
    let cur: string | undefined = hoveredNodeId;
    while (cur) {
      ids.add(cur);
      cur = parentMap.get(cur);
    }
    return ids;
  }, [hoveredNodeId, parentMap]);

  // Node IDs hidden because their type legend is toggled off (cascades to all descendants)
  const nodeTypeHiddenIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (hiddenLegend.size === 0) return ids;
    // Seed: nodes whose own type is directly toggled off
    const queue: string[] = [];
    nodes.forEach((n) => {
      const key = Object.keys(LEGEND_NODE_TYPE).find(
        (k) => LEGEND_NODE_TYPE[k] === n.type,
      );
      if (key && hiddenLegend.has(key)) queue.push(n.id);
    });
    // BFS: cascade hide to all descendants via the edge tree
    while (queue.length > 0) {
      const id = queue.pop()!;
      if (ids.has(id)) continue;
      ids.add(id);
      childrenMap.get(id)?.forEach((childId) => queue.push(childId));
    }
    return ids;
  }, [nodes, childrenMap, hiddenLegend]);

  // Target node IDs of edges that are hidden by flow-type legend
  const flowHiddenTargets = React.useMemo(() => {
    const targets = new Set<string>();
    const hideHealthy = hiddenLegend.has("Running flow");
    const hideUnhealthy = hiddenLegend.has("Stopped flow");
    if (!hideHealthy && !hideUnhealthy) return targets;
    edges.forEach((e) => {
      const healthy = (e.data as any)?.healthy !== false;
      if ((healthy && hideHealthy) || (!healthy && hideUnhealthy)) {
        targets.add(e.target);
      }
    });
    return targets;
  }, [edges, hiddenLegend]);

  const displayNodes = React.useMemo(() => {
    return nodes.map((n) => {
      if (nodeTypeHiddenIds.has(n.id) || flowHiddenTargets.has(n.id))
        return { ...n, hidden: true };
      if (hoveredNodeId !== null) {
        const isAncestor = ancestorIds.has(n.id);
        return { ...n, data: { ...n.data, __dim: !isAncestor } };
      }
      return n;
    });
  }, [nodes, nodeTypeHiddenIds, flowHiddenTargets, hoveredNodeId, ancestorIds]);

  const displayEdges = React.useMemo(() => {
    const hideHealthy = hiddenLegend.has("Running flow");
    const hideUnhealthy = hiddenLegend.has("Stopped flow");
    return edges.map((e) => {
      const healthy = (e.data as any)?.healthy !== false;
      if ((healthy && hideHealthy) || (!healthy && hideUnhealthy))
        return { ...e, hidden: true };
      if (nodeTypeHiddenIds.has(e.source) || nodeTypeHiddenIds.has(e.target))
        return { ...e, hidden: true };
      // Hide edges that are not part of the hovered ancestor path
      if (hoveredNodeId !== null) {
        const onPath = ancestorIds.has(e.source) && ancestorIds.has(e.target);
        if (!onPath) return { ...e, hidden: true };
      }
      return e;
    });
  }, [edges, nodeTypeHiddenIds, hiddenLegend, hoveredNodeId, ancestorIds]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="size-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!topo) return null;

  if (topo.vpcs.length === 0 && topo.globalServices.totalResources === 0) {
    return (
      <div className="py-16 text-center border border-dashed border-border rounded-xl">
        <Cloud className="size-10 d mx-auto mb-3" />
        <p className="d text-sm">
          No resources found. Run a scan to populate.
        </p>
      </div>
    );
  }

  const tbBtnStyle: React.CSSProperties = {
    fontSize: 11,
    padding: "3px 10px",
    borderRadius: 6,
    border: `1px solid ${th.btnBorder}`,
    background: th.btnBg,
    color: th.btnColor,
    cursor: "pointer",
  };

  return (
    <div
      ref={containerRef}
      className="relative flex flex-col rounded-xl border overflow-hidden"
      style={{ height: "90vh", minHeight: 520, background: th.containerBg, borderColor: th.toolbarBorder }}
    >
      {/* Toolbar */}
      <div
        className="relative z-10 flex-shrink-0"
        style={{
          background: th.toolbarBg,
          borderBottom: `1px solid ${th.toolbarBorder}`,
        }}
      >
        {/* Row 1 — stats + expand/collapse */}
        <div
          className="flex items-center justify-between px-4 py-2"
          style={{ borderBottom: `1px solid ${th.rowBorder}` }}
        >
          <p style={{ fontSize: 11, color: th.textMuted }}>
            <span style={{ fontWeight: 600, color: th.textStrong }}>
              {nodes.length}
            </span>{" "}
            Nodes ·{" "}
            <span style={{ fontWeight: 600, color: th.textStrong }}>
              {edges.length}
            </span>{" "}
            Edges ·{" "}
            <span style={{ fontWeight: 600, color: "#f59e0b" }}>
              {topo.totalResources}
            </span>{" "}
            Resources
          </p>
          {/* <div className="flex items-center gap-2">
            <button onClick={handleExpandAll} style={tbBtnStyle}>
              Expand All
            </button>
            <button onClick={handleCollapseAll} style={tbBtnStyle}>
              Collapse
            </button>
          </div> */}
        </div>

        {/* Row 2 — search + cascade filters */}
        <div className="flex items-center gap-2 px-4 py-2 flex-wrap">
          <div style={{ position: "relative", width: 220 }}>
            <Search
              className="size-3.5"
              style={{
                position: "absolute",
                left: 9,
                top: "50%",
                transform: "translateY(-50%)",
                color: th.iconColor,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search VPC, service, resource…"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: "100%",
                paddingLeft: 30,
                paddingRight: searchQuery ? 28 : 10,
                paddingTop: 5,
                paddingBottom: 5,
                fontSize: 12,
                background: th.inputBg,
                border: `1px solid ${th.inputBorder}`,
                borderRadius: 7,
                color: th.inputColor,
                outline: "none",
              }}
            />
            {searchQuery && (
              <button
                onClick={() => handleSearch("")}
                style={{
                  position: "absolute",
                  right: 7,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: th.iconColor,
                  padding: 0,
                }}
              >
                <X className="size-3" />
              </button>
            )}
          </div>

          <ChevronRight
            className="size-3.5 flex-shrink-0"
            style={{ color: th.chevronColor }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <MapPin
              className="size-3.5 flex-shrink-0"
              style={{ color: "#f59e0b" }}
            />
            <Dropdown
              size="sm"
              variant="pill"
              value={regionFilter}
              onChange={handleRegionFilter}
              options={[
                { value: "all", label: "All Regions" },
                ...regionNames.map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>

          <ChevronRight
            className="size-3.5 flex-shrink-0"
            style={{ color: th.chevronColor }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Network
              className="size-3.5 flex-shrink-0"
              style={{ color: "#38bdf8" }}
            />
            <Dropdown
              size="sm"
              variant="pill"
              disabled={vpcsForFilter.length === 0}
              value={vpcFilter}
              onChange={handleVpcFilter}
              options={[
                { value: "all", label: "All VPCs" },
                ...vpcsForFilter.map((v) => ({
                  value: v,
                  label: v.length > 22 ? v.slice(0, 20) + "…" : v,
                })),
              ]}
            />
          </div>

          <ChevronRight
            className="size-3.5 flex-shrink-0"
            style={{ color: th.chevronColor }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <Server
              className="size-3.5 flex-shrink-0"
              style={{ color: "#818cf8" }}
            />
            <Dropdown
              size="sm"
              variant="pill"
              disabled={servicesForFilter.length === 0}
              value={serviceFilter}
              onChange={handleServiceFilter}
              options={[
                { value: "all", label: "All Services" },
                ...servicesForFilter.map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>

          {(regionFilter !== "all" ||
            vpcFilter !== "all" ||
            serviceFilter !== "all") && (
            <button
              onClick={() => handleRegionFilter("all")}
              style={{
                fontSize: 11,
                padding: "3px 8px",
                borderRadius: 6,
                border: "1px solid rgba(239,68,68,0.3)",
                background: "rgba(239,68,68,0.1)",
                color: "#f87171",
                cursor: "pointer",
                marginLeft: 4,
              }}
            >
              Clear Filters
            </button>
          )}

          <span
            style={{ fontSize: 10, color: th.hintColor, marginLeft: "auto" }}
          >
            {serviceFilter !== "all"
              ? "Showing resources · click to view details"
              : "Select Region → VPC → Service to drill in"}
          </span>
        </div>

        {/* Row 3 — legend */}
        <div
          className="flex items-center gap-2 px-4 py-1.5 flex-wrap"
          style={{ borderTop: `1px solid ${th.rowBorder}` }}
        >
          {[
            { label: "Account", color: "#f59e0b" },
            { label: "Region", color: "#f97316" },
            { label: "VPC", color: "#38bdf8" },
            { label: "Service", color: "#818cf8" },
            { label: "Resource", color: "#64748b" },
            { label: "Running flow", color: "#22c55e" },
            { label: "Stopped flow", color: "#ef4444" },
          ].map((item) => {
            const hidden = hiddenLegend.has(item.label);
            return (
              <Tip
                key={item.label}
                label={hidden ? `Show ${item.label}` : `Hide ${item.label}`}
                side="top"
              >
              <button
                onClick={() => toggleLegend(item.label)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontSize: 10,
                  color: hidden
                    ? isDark ? "rgba(255,255,255,0.35)" : "#9ca3af"
                    : th.legendColor,
                  padding: "2px 7px",
                  borderRadius: 999,
                  border: `1px solid ${hidden ? "transparent" : th.legendBorder}`,
                  background: hidden ? "transparent" : th.legendBg,
                  cursor: "pointer",
                  opacity: hidden ? 0.5 : 1,
                  textDecoration: hidden ? "line-through" : "none",
                  transition: "opacity 0.2s, background 0.2s, border-color 0.2s",
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: hidden ? "#6b7280" : item.color,
                    flexShrink: 0,
                    transition: "background 0.2s",
                  }}
                />
                {item.label}
              </button>
              </Tip>
            );
          })}
        </div>
      </div>

      {/* React Flow canvas */}
      <div className="flex-1 relative min-h-0">
        <ReactFlow
          nodes={displayNodes}
          edges={displayEdges}
          onNodesChange={locked ? undefined : onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={tnNodeTypes}
          edgeTypes={tnEdgeTypes}
          fitView
          fitViewOptions={{ padding: 0.12 }}
          onInit={(instance) => {
            setFlowRef(instance);
            setTimeout(() => {
              // Zoom level is derived from how many nodes the account actually
              // has, so a 3-node subscription does not open at the same
              // aggressive zoom as a 40-node one. Falls back to whatever
              // fitView picked if the graph is somehow still empty.
              const visibleCount = nodesRef.current.filter(
                (n) => !n.hidden,
              ).length;
              const targetZoom = visibleCount
                ? topologyInitialZoom(visibleCount, {
                    minZoom: TOPO_MIN_ZOOM,
                    maxZoom: TOPO_MAX_ZOOM,
                  })
                : instance.getViewport().zoom;
              instance.zoomTo(targetZoom, { duration: 900 });
              initialZoomRef.current = targetZoom;
            }, 350);
          }}
          onNodeClick={handleNodeClick}
          onNodeMouseEnter={(_: any, node: Node) => setHoveredNodeId(node.id)}
          onNodeMouseLeave={() => setHoveredNodeId(null)}
          minZoom={TOPO_MIN_ZOOM}
          maxZoom={TOPO_MAX_ZOOM}
          zoomOnScroll
          panOnDrag
          nodesDraggable={!locked}
          nodesConnectable={false}
          proOptions={{ hideAttribution: true }}
          style={{ background: "transparent", width: "100%", height: "100%" }}
        >
          <Background
            color={th.bgPatternColor}
            gap={32}
            size={isDark ? 1.2 : 1}
            variant={isDark ? ("dots" as any) : ("lines" as any)}
          />
          <MiniMap
            nodeColor={(n) =>
              n.type === "tnAccount"
                ? "#f59e0b"
                : n.type === "tnRegion"
                  ? "#f97316"
                  : n.type === "tnVpc"
                    ? "#38bdf8"
                    : n.type === "tnService"
                      ? "#818cf8"
                      : "#475569"
            }
            maskColor={th.minimapMask}
            style={{
              background: th.minimapBg,
              border: `1px solid ${th.minimapBorder}`,
              borderRadius: 10,
              bottom: 16,
              right: 60,
            }}
            zoomable
            pannable
          />
        </ReactFlow>

        {/* Service resource panel — inside canvas, not covering toolbar */}
        {selectedSvc && (
          <SvcResourcePanel
            service={selectedSvc}
            onClose={() => {
              setSelectedSvc(null);
              setNodes((nds) =>
                nds.map((n) =>
                  n.type === "tnService"
                    ? { ...n, data: { ...n.data, __selected: false } }
                    : n,
                ),
              );
            }}
            onView={handleViewTopoResource}
          />
        )}
      </div>

      {/* Floating control panel */}
      <TopologyControls
        onZoomIn={() => flowRef?.zoomIn({ duration: 200 })}
        onZoomOut={() => flowRef?.zoomOut({ duration: 200 })}
        onFitView={() => {
          if (!fitViewMode) {
            flowRef?.fitView({ padding: 0.2, duration: 400 });
            setFitViewMode(true);
          } else {
            if (initialZoomRef.current !== null) {
              flowRef?.zoomTo(initialZoomRef.current, { duration: 400 });
            }
            setFitViewMode(false);
          }
        }}
        onFullscreen={toggleFullscreen}
        locked={locked}
        onToggleLock={() => setLocked((l) => !l)}
        isFullscreen={isFullscreen}
        isDark={isDark}
      />

      {/* Loading overlay for topology resource detail */}
      {loadingTopoDetail && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/30">
          <div className="size-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Resource detail panel — full-page overlay */}
      {topoResource && (
        <ResourceDetailPanel
          resource={topoResource}
          onClose={() => setTopoResource(null)}
        />
      )}
    </div>
  );
}

// ── Resources Tab (flat table) ────────────────────────────────────────────────

type CloudResourceSummary = AwsResourceListResponse & {
  lastSyncedAt?: string | null;
};

function ResourcesTab({
  cloudProviderId,
  onViewResource,
}: {
  cloudProviderId: string;
  onViewResource: (r: CloudResourceSummary) => void;
}) {
  /* Rows for the current page — the backend does the searching, filtering and
     paging, so this only ever holds `pageSize` records. */
  const [rows, setRows] = useState<CloudResourceSummary[]>([]);
  const [total, setTotal] = useState(0);
  /* The whole set, fetched once, purely to populate the cascading filter
     dropdowns. A single page can't tell us which regions/networks/services
     exist on the account. */
  const [facetSource, setFacetSource] = useState<CloudResourceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  /* `nameSearch` is what's in the box; `keyword` is what's been submitted. */
  const [nameSearch, setNameSearch] = useState("");
  const [keyword, setKeyword] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [vpcFilter, setVpcFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const firstLoadDoneRef = useRef(false);

  useEffect(() => {
    let isCurrent = true;
    listCloudProviderResources(cloudProviderId)
      .then((res) => {
        if (isCurrent) setFacetSource(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        /* Non-fatal: the table still loads, the dropdowns just stay empty. */
      });
    return () => {
      isCurrent = false;
    };
  }, [cloudProviderId]);

  const loadResources = useCallback(async () => {
    /* Skeleton only before the first page lands — later fetches keep the
       current rows on screen instead of blanking the table. */
    if (!firstLoadDoneRef.current) setLoading(true);
    try {
      const res = await searchCloudProviderResources(
        cloudProviderId,
        page,
        pageSize,
        {
          keyword,
          sortByColumn: "",
          sortingOrder: "",
          filters: {
            region: regionFilter,
            vpcId: vpcFilter === "__global__" ? null : vpcFilter,
            isGlobal: vpcFilter === "__global__" ? "true" : null,
            serviceName: serviceFilter,
            status: statusFilter,
          },
        },
      );
      setRows(res.items as CloudResourceSummary[]);
      setTotal(res.totalItems);
      /* A narrowed filter can leave us past the last page — step back. */
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch {
      useToastStore.getState().showError("Error", "Failed to load resources");
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [
    cloudProviderId,
    page,
    pageSize,
    keyword,
    regionFilter,
    vpcFilter,
    serviceFilter,
    statusFilter,
  ]);

  useEffect(() => {
    loadResources();
  }, [loadResources]);

  /* Search fires on Enter or the icon, never per keystroke — but clearing the
     box restores the unfiltered list straight away. */
  const submitResourceSearch = (raw?: string) => {
    const next = (raw ?? nameSearch).trim();
    setPage(0);
    setKeyword(next);
  };

  const handleFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(0);
  };

  const regionOptions = Array.from(
    new Set(facetSource.map((r) => r.region).filter(Boolean)),
  ).sort();
  const vpcOptions = Array.from(
    new Set(
      facetSource
        .filter((r) => !regionFilter || r.region === regionFilter)
        .map((r) => (r.isGlobal ? "__global__" : r.vpcId || ""))
        .filter(Boolean),
    ),
  );
  const serviceOptions = Array.from(
    new Set(
      facetSource
        .filter((r) => !regionFilter || r.region === regionFilter)
        .filter(
          (r) =>
            !vpcFilter ||
            (vpcFilter === "__global__" ? r.isGlobal : r.vpcId === vpcFilter),
        )
        .map((r) => r.serviceName || "")
        .filter(Boolean),
    ),
  ).sort();

  const columns: Column<CloudResourceSummary>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-14",
      align: "center",
      render: (_, i) => (
        <span className="d text-sm tabular-nums whitespace-nowrap">
          {page * pageSize + i + 1}
        </span>
      ),
    },
    {
      key: "serviceName",
      header: "Service",
      width: "w-44",
      align: "left",
      render: (row) => (
        <div className="flex items-center gap-2">
          <Cloud className="size-3.5 d flex-shrink-0" />
          <span className="text-sm font-medium truncate">
            {row.serviceName || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "_resource",
      header: "Resource",
      align: "left",
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate max-w-xs">
            {row.resourceName || row.resourceId || "—"}
          </p>
          {row.resourceName && (
            <p className="text-xs font-mono d truncate max-w-xs">
              {row.resourceId}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "region",
      header: "Region",
      width: "w-36",
      align: "left",
      render: (row) => (
        <span className="text-sm whitespace-nowrap">{row.region || "—"}</span>
      ),
    },
    {
      key: "vpcId",
      header: "Network",
      width: "w-40",
      align: "left",
      render: (row) => (
        <span className="text-sm d font-mono truncate block">
          {row.vpcId || (row.isGlobal ? "Global" : "—")}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-32",
      align: "center",
      render: (row) => (
        <span className="inline-block whitespace-nowrap">
          <StatusBadge status={row.status} />
        </span>
      ),
    },
    {
      key: "lastSyncedAt",
      header: "Last Synced",
      width: "w-36",
      align: "left",
      render: (row) => (
        <span className="text-xs d whitespace-nowrap">
          {row.lastSyncedAt ? formatDate(row.lastSyncedAt) : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <button
            type="button"
            aria-label="Search resources"
            onClick={() => submitResourceSearch()}
            className="absolute left-3 top-1/2 -translate-y-1/2 d hover:text-foreground transition-colors"
          >
            <Search className="size-4" />
          </button>
          <input
            type="text"
            placeholder="Search by name or ID"
            value={nameSearch}
            aria-label="Search resources"
            onChange={(e) => {
              const next = e.target.value;
              setNameSearch(next);
              /* Emptying the box is itself a search: restore the full list. */
              if (!next.trim() && keyword) submitResourceSearch("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitResourceSearch();
            }}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
          />
        </div>
        <Dropdown
          variant="pill"
          className="w-44"
          placeholder="All Regions"
          value={regionFilter}
          onChange={(v) => {
            setRegionFilter(v);
            setVpcFilter("");
            setServiceFilter("");
            setPage(0);
          }}
          options={[
            { value: "", label: "All Regions" },
            ...regionOptions.map((region) => ({
              value: region,
              label: region,
            })),
          ]}
        />

        <Dropdown
          variant="pill"
          className="w-56"
          placeholder="All Networks"
          value={vpcFilter}
          onChange={(v) => {
            setVpcFilter(v);
            setServiceFilter("");
            setPage(0);
          }}
          options={[
            { value: "", label: "All Networks" },
            ...vpcOptions
              .filter((v) => v !== "__global__")
              .map((vpcId) => ({ value: vpcId, label: vpcId })),
            ...(vpcOptions.includes("__global__")
              ? [{ value: "__global__", label: "Global Services" }]
              : []),
          ]}
        />

        <Dropdown
          variant="pill"
          className="w-52"
          placeholder="All Services"
          value={serviceFilter}
          onChange={(v) => handleFilterChange(setServiceFilter)(v)}
          options={[
            { value: "", label: "All Services" },
            ...serviceOptions.map((svc) => ({ value: svc, label: svc })),
          ]}
        />

        <Dropdown
          variant="pill"
          className="w-40"
          placeholder="All Statuses"
          value={statusFilter}
          onChange={(v) => handleFilterChange(setStatusFilter)(v)}
          options={[
            { value: "", label: "All Statuses" },
            ...[
              "running",
              "stopped",
              "available",
              "pending",
              "terminated",
              "failed",
            ].map((s) => ({
              value: s,
              label: s.charAt(0).toUpperCase() + s.slice(1),
              dot:
                s === "running" || s === "available"
                  ? "bg-emerald-500"
                  : s === "stopped" || s === "terminated"
                    ? "bg-slate-400"
                    : s === "pending"
                      ? "bg-amber-500"
                      : s === "failed"
                        ? "bg-red-500"
                        : undefined,
            })),
          ]}
        />

        <div className="ml-auto flex items-center gap-2 self-center">
          <RowsPerCardSelect
            value={pageSize}
            onChange={(n) => {
              setPageSize(n);
              setPage(0);
            }}
            options={[10, 25, 50, 100]}
            label="Rows per page"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyText="No resources match the current filter."
        onRowClick={(row) => onViewResource(row)}
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        itemLabel="resources"
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function CMDBCloudAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [account, setAccount] = useState<CloudProviderResponse | null>(null);
  // Publish the open cloud account, so cmdb.yaml's {item} questions apply. The CLOUD_ACCOUNTS view
  // comes from the route. Cleared on unmount.
  useEffect(() => {
    const name = account?.accountName;
    useUiContextStore
      .getState()
      .setSelectedItem(id && name ? { id, name } : null);
    return () => useUiContextStore.getState().clearSelectedItem();
  }, [id, account]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"topology" | "resources">(
    "topology",
  );

  const [selectedResource, setSelectedResource] = useState<AwsResource | null>(
    null,
  );
  const [loadingDetail, setLoadingDetail] = useState(false);

  // ── Discovery — delegated to global store ──
  const { runs: discoveryRuns, startDiscovery } = useDiscoveryStore();
  const discovering = id ? discoveryRuns[id]?.status === "RUNNING" : false;

  const handleDiscover = useCallback(() => {
    if (!id || !account) return;
    startDiscovery(id, account.accountName);
  }, [id, account, startDiscovery]);

  useEffect(() => {
    if (!id) return;
    let isCurrent = true;
    setLoading(true);
    setAccount(null);
    setSelectedResource(null);
    setActiveTab("topology");
    getCloudProvider(id)
      .then((accountRes) => {
        if (isCurrent) setAccount(accountRes.data);
      })
      .catch(() => {
        if (isCurrent) {
          useToastStore
            .getState()
            .showError("Error", "Failed to load cloud account");
        }
      })
      .finally(() => {
        if (isCurrent) setLoading(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [id]);

  const handleViewResource = async (r: CloudResourceSummary) => {
    setLoadingDetail(true);
    try {
      const res = await getAwsResource(r.id);
      setSelectedResource(res.data);
    } catch {
      useToastStore
        .getState()
        .showError("Error", "Failed to load resource detail");
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="flex items-center gap-3 d">
          <div className="size-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          Loading account summary…
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="py-16 text-center">
        <Cloud className="size-10 d mx-auto mb-3" />
        <p className="d">Account not found or no resources available.</p>
        <button
          onClick={() => navigate("/cmdb/cloud-accounts")}
          className="mt-4 text-sm text-indigo-500 hover:text-indigo-400"
        >
          ← Back to Cloud Accounts
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        parent="Back to Cloud Accounts"
        parentTo="/cmdb/cloud-accounts"
        current={account.accountName}
        actions={
          <>
            {/* <span className="px-2 py-0.5 rounded text-xs font-mono bg-muted d">
              {account.accountId}
            </span> */}
            <div className="flex items-center bg-muted/40 border border-border rounded-lg p-0.5 gap-0.5">
              <button
                onClick={() => setActiveTab("topology")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeTab === "topology"
                    ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                    : "d hover:text-foreground hover:bg-muted/60"
                }`}
              >
                <Network className="size-3.5" /> Topology
              </button>
              <button
                onClick={() => setActiveTab("resources")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                  activeTab === "resources"
                    ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/30"
                    : "d hover:text-foreground hover:bg-muted/60"
                }`}
              >
                <Server className="size-3.5" /> Resources
              </button>
            </div>
            <button
              onClick={handleDiscover}
              disabled={discovering}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              {discovering ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" /> Discovering…
                </>
              ) : (
                <>
                  <Zap className="size-3.5" /> Discover Resources
                </>
              )}
            </button>
          </>
        }
      />

      {/* Tab Content */}
      {activeTab === "topology" && (
        <CloudTopologyView
          providerId={id!}
        />
      )}
      {activeTab === "resources" && (
        <ResourcesTab
          // Remounting on account change resets search/filters/paging without a
          // reset effect that would fire a throwaway fetch with stale filters.
          key={id}
          cloudProviderId={id!}
          onViewResource={handleViewResource}
        />
      )}

      {/* Loading overlay for detail */}
      {loadingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="size-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Resource detail panel */}
      {selectedResource && (
        <ResourceDetailPanel
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
        />
      )}
    </div>
  );
}
